import React, { useState } from "react";
import Layout from "../../components/layout/Layout";
import Moment from "react-moment";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  PencilAltIcon,
  TrashIcon,
  XIcon,
} from "@heroicons/react/solid";
import Pagination from "../../components/pagination";
import { Modal } from '@mui/material';
import toast from "react-hot-toast";
import useGet from "../../hooks/read/useGet";
import usePut from "../../hooks/usePut";
import "react-datepicker/dist/react-datepicker.css";
import AddAttendanceRecord from './AddAttendanceRecord';
import EditAttendanceRecord from './EditAttendanceRecord';

function AttendanceRecords() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const employeeName = searchParams.get('employee_name') || 'Unknown Employee';
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false);
  const [selectedAttendanceId, setSelectedAttendanceId] = useState(null);
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleOpenCreateDrawer = () => {
    setCreateDrawerOpen(true);
  };

  const handleCloseCreateDrawer = () => {
    setCreateDrawerOpen(false);
  };

  const handleOpenEditDrawer = (attendanceId) => {
    setSelectedAttendanceId(attendanceId);
    setEditDrawerOpen(true);
  };

  const handleCloseEditDrawer = () => {
    setEditDrawerOpen(false);
  };

  const initialState = {
    pageNo: searchParams.get("pageNo") || 0,
    limit: searchParams.get("limit") || 10,
    user_id: id,
  };

  const {
    list: attendanceData,
    paramsObject,
    setGetListParams,
  } = useGet({
    url: "employee_yearly_attendance_list",
    initialData: initialState,
    onSuccess: () => {
      // toast.success('Attendance Records Fetched Successfully')
    },
    onError: () => {
      toast.error("Error fetching attendance records");
    },
  });

  const { handleAuth: deleteAttendance } = usePut({
    url: "delete_employee_yearly_attendance",
    refreshUrl: '/employee_yearly_attendance_list',
    onSuccess: () => {
      toast.success('Attendance record deleted successfully');
      setDeleteConfirmationModal(false);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Error deleting attendance record");
    },
  });

  const { handleAuth: createAttendance } = usePut({
    url: "create_employee_yearly_attendance",
    refreshUrl: '/employee_yearly_attendance_list',
    onSuccess: () => {
      toast.success('Attendance record created successfully');
      handleCloseCreateDrawer();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Error creating attendance record");
    },
  });

  const handlePageClick = (val) => {
    let data = {
      ...paramsObject,
      pageNo: val.selected,
    };
    setGetListParams(data);
    setSearchParams(data);
  };

  const handleOpenDeleteModal = (attendanceId) => {
    setSelectedAttendanceId(attendanceId);
    setDeleteConfirmationModal(true);
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      await deleteAttendance.mutateAsync({
        employee_yearly_attendance_id: selectedAttendanceId,
      });
    } catch (error) {
      console.error("Error deleting attendance record:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCreateAttendance = async (data) => {
    try {
      setCreateLoading(true);
      await createAttendance.mutateAsync(data);
    } catch (error) {
      console.error("Error creating attendance record:", error);
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <Layout>
      <div className="pb-4 px-4 flex items-center space-x-3">
        <ArrowLeftIcon
          className="text-gray-700 w-8 h-8 cursor-pointer"
          onClick={() => navigate(-1)}
        />
        <h1 className="font-semibold text-lg">
          Attendance Records of{" "}
          <span className="italic bg-purple-200 text-purple-700 px-5 py-1 rounded">
            {employeeName}
          </span>
        </h1>
      </div>

      <div className="flex items-center justify-between px-4 mb-10">
        <button
          className=" bg-[color:var(--color1)] text-white rounded px-4 py-4 text-lg" 
          onClick={handleOpenCreateDrawer}
        >
          Add Attendance Record
        </button>
      </div>

      <div className="max-w-full overflow-x-auto px-4 mt-8">
        {attendanceData.isLoading ? (
          <div className="text-center">Loading...</div>
        ) : attendanceData?.data?.data?.length <= 0 ? (
          <div className="text-center">
            <p className="text-gray-700 text-3xl">No Attendance Records To Show</p>
          </div>
        ) : (
          <>
            <table className="table-auto w-full">
              <thead>
                <tr className="bg-[color:var(--color1)] text-center">
                  <th className="w-1/7 min-w-[100px] text-lg font-semibold text-white py-3 lg:py-3 px-3 lg:px-4">
                    Year
                  </th>
                  <th className="w-1/7 min-w-[160px] text-lg font-semibold text-white py-3 lg:py-3 px-3 lg:px-4">
                    From Date
                  </th>
                  <th className="w-1/7 min-w-[160px] text-lg font-semibold text-white py-3 lg:py-3 px-3 lg:px-4">
                    To Date
                  </th>
                  <th className="w-1/7 min-w-[100px] text-lg font-semibold text-white py-3 lg:py-3 px-3 lg:px-4">
                    Present Days
                  </th>
                  <th className="w-1/7 min-w-[100px] text-lg font-semibold text-white py-3 lg:py-3 px-3 lg:px-4">
                    Absent Days
                  </th>
                  <th className="w-1/7 min-w-[100px] text-lg font-semibold text-white py-3 lg:py-3 px-3 lg:px-4">
                    Paid Leaves
                  </th>
                  <th className="w-1/7 min-w-[100px] text-lg font-semibold text-white py-3 lg:py-3 px-3 lg:px-4">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {attendanceData?.data?.data?.map((data, idx) => (
                  <tr key={idx}>
                    <td className="text-center text-dark font-medium text-base py-3 px-2 bg-[#F3F6FF] border-b border-[#E8E8E8]">
                      {data.year}
                    </td>
                    <td className="text-center text-dark font-medium text-base py-3 px-2 bg-[#F3F6FF] border-b border-[#E8E8E8]">
                      <Moment format="ll">{data.from_date * 1000}</Moment>
                    </td>
                    <td className="text-center text-dark font-medium text-base py-3 px-2 bg-[#F3F6FF] border-b border-[#E8E8E8]">
                      <Moment format="ll">{data.to_date * 1000}</Moment>
                    </td>
                    <td className="text-center text-dark font-medium text-base py-3 px-2 bg-[#F3F6FF] border-b border-[#E8E8E8]">
                      {data.present_days}
                    </td>
                    <td className="text-center text-dark font-medium text-base py-3 px-2 bg-[#F3F6FF] border-b border-[#E8E8E8]">
                      {data.absent_days}
                    </td>
                    <td className="text-center text-dark font-medium text-base py-3 px-2 bg-[#F3F6FF] border-b border-r border-[#E8E8E8]">
                      {data.paid_leave}
                    </td>
                    <td className="text-center text-dark font-medium text-base py-3 px-2 bg-[#F3F6FF] border-b border-r border-[#E8E8E8]">
                      <div className="flex justify-center space-x-1">
                        <button
                          className="flex items-center"
                          onClick={() => handleOpenEditDrawer(data.employee_yearly_attendance_id)}
                        >
                          <PencilAltIcon className="text-gray-600 w-6 hover:text-blue-600" />
                        </button>
                        <div className="">
                          <TrashIcon
                            className="text-gray-600 w-6 hover:text-orange-600 cursor-pointer"
                            onClick={() => handleOpenDeleteModal(data.employee_yearly_attendance_id)}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-5 mt-5 mb-10">
              <Pagination
                currentPage={+paramsObject.pageNo}
                lengthofItems={attendanceData.data?.count}
                limit={+paramsObject.limit}
                onPageChange={handlePageClick}
              />
            </div>
          </>
        )}
      </div>
      <Modal open={deleteConfirmationModal}>
        <div className='max-w-lg w-full bg-white absolute left-1/2 -translate-x-1/2 top-[20%] mx-auto'>
          <div className='document-wrapper px-4 divide-y'>
            <div className="document-head py-4">
              <div className='flex items-center justify-between'>
                <h3 className='text-gray-700 text-3xl font-semibold'>Delete Attendance Record</h3>
                <div className='w-6 h-6 cursor-pointer' onClick={() => setDeleteConfirmationModal(false)}>
                  <XIcon className='text-black w-full h-full' />
                </div>
              </div>
            </div>
            <div className="document-body max-h-96 overflow-auto py-4">
              <div>
                <p className='roboto text-center font-bold text-2xl text-gray-700 mb-4'>
                  Are you sure you want to delete this attendance record?
                </p>
                <div className="space-x-3 text-center">
                  <button 
                    className='px-4 py-2 rounded-md font-semibold bg-red-200 text-red-700 border-red-800'
                    onClick={handleDelete}
                    disabled={deleteLoading}
                  >
                    {deleteLoading ? 'Deleting...' : 'Yes'}
                  </button>
                  <button 
                    className='px-4 py-2 rounded-md font-semibold border'
                    onClick={() => setDeleteConfirmationModal(false)}
                    disabled={deleteLoading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
      <AddAttendanceRecord
        open={createDrawerOpen}
        onClose={handleCloseCreateDrawer}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        userId={id}
        onCreate={handleCreateAttendance}
        loading={createLoading}
      />
      {editDrawerOpen && (
        <EditAttendanceRecord
          open={editDrawerOpen}
          onClose={handleCloseEditDrawer}
          attendanceId={selectedAttendanceId}
        />
      )}
    </Layout>
  );
}

export default AttendanceRecords;
